import { motion } from "framer-motion";
import {
	Circle,
	Facebook,
	Instagram,
	Menu,
	Target,
	Twitter
} from "lucide-react";
import { useEffect, useState } from "react";

import HelpPage from "@/components/dashboard/help-page";
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { createFileRoute, Link, useRouter } from "@tanstack/react-router";

export const Route = createFileRoute("/")({
	component: RouteComponent
});

const termsAndConditions = [
	{
		id: "introduction",
		title: "1. INTRODUCTION",
		content: (
			<p>
				Welcome to Intasida, a digital platform that connects online vendors and
				buyers, facilitating seamless transactions between both parties. By
				accessing and using our website, you agree to be bound by these Terms
				and Conditions. If you do not agree with any part of these Terms, please
				discontinue use of our services immediately.
			</p>
		)
	},
	{
		id: "user-agreement",
		title: "2. USER AGREEMENT",
		content: (
			<p>
				By using this website, you confirm that you have read, understood, and
				agreed to abide by these Terms and Conditions. You also consent to the
				collection, processing, and storage of personal data provided on the
				platform in accordance with our Privacy Policy.
			</p>
		)
	},
	{
		id: "personal-data",
		title: "3. PERSONAL DATA PROCESSING",
		content: (
			<p>
				By using Intasida, you acknowledge and consent to the collection and
				processing of your personal data, including but not limited to your
				name, contact details, and transaction history. This information will be
				used to facilitate transactions, enhance user experience, and comply
				with regulatory requirements. For more details on how we handle your
				personal data, please refer to our Privacy Policy.
			</p>
		)
	},
	{
		id: "role-of-intasida",
		title: "4. ROLE OF INTASIDA",
		content: (
			<p>
				Intasida acts as an intermediary between online vendors and buyers. We
				do not own, manufacture, or directly sell any goods listed on our
				platform. Any disputes concerning products purchased must be resolved
				between the vendor and the buyer, subject to our dispute resolution
				process.
			</p>
		)
	}
];

const privacyPolicy = [
	{
		id: "introduction",
		title: "1. INTRODUCTION",
		content: (
			<p>
				Intasida Limited ("Intasida," "Company," "we," "us," or "our") is
				committed to protecting the rights and freedoms of data subjects
				(customers, employees, and partners) and ensuring the safe and secure
				processing of their personal data in compliance with the Nigeria Data
				Protection Act 2023, the Central Bank of Nigeria (CBN) Guidelines on
				Data Protection for Financial Institutions, and other applicable
				financial regulatory frameworks.
				<br />
				<br />
				We process personal data to provide secure and efficient financial
				services, ensure regulatory compliance, prevent fraud, and enhance user
				experience. This policy outlines our data protection commitments and
				ensures our employees and partners understand the principles governing
				the use of personal data.
			</p>
		)
	},
	{
		id: "definitions",
		title: "2. DEFINITIONS",
		content: (
			<ul className="list-disc pl-5 space-y-1">
				<li>
					<strong>Personal Data:</strong>
					{" "}
					Any information that identifies an
					individual, including name, phone number, email, financial details,
					account numbers, and transaction history.
				</li>
				<li>
					<strong>Special Category Data:</strong>
					{" "}
					Sensitive data including
					biometric data, health information, and criminal records, which
					require enhanced protection.
				</li>
				<li>
					<strong>Data Controller:</strong>
					{" "}
					Intasida Limited, determining the
					purpose and means of data processing.
				</li>
				<li>
					<strong>Data Processor:</strong>
					{" "}
					Any third party processing personal
					data on behalf of Intasida.
				</li>
				<li>
					<strong>Processing:</strong>
					{" "}
					Any operation performed on personal data,
					including collection, storage, transmission, and deletion.
				</li>
				<li>
					<strong>Supervisory Authority:</strong>
					{" "}
					The National Data Protection
					Commission (NDPC) and other relevant financial regulatory bodies.
				</li>
			</ul>
		)
	},
	{
		id: "scope",
		title: "3. SCOPE",
		content: (
			<p>
				This policy applies to all personal data processed by Intasida Limited,
				including data collected from customers, employees, vendors, and
				third-party service providers.
			</p>
		)
	},
	{
		id: "purpose",
		title: "4. PURPOSE",
		content: (
			<div>
				<p>Intasida processes personal data for:</p>
				<ul className="list-disc pl-5 space-y-1">
					<li>Marketing and customer engagement (with consent)</li>
					<li>Financial transactions, including payments and loan services</li>
					<li>
						Fraud detection and compliance with anti-money laundering (AML)
						regulations
					</li>
					<li>Customer support and account management</li>
					<li>Internal business operations and human resources management</li>
				</ul>
			</div>
		)
	},
	{
		id: "procedures",
		title: "5. OUR PROCEDURES",
		content: (
			<div>
				<h3 className="text-lg font-medium mt-2 mb-1">
					5.1 Lawful Bases for Processing
				</h3>
				<p>
					Processing of personal data shall be lawful, fair, and transparent. We
					rely on the following lawful bases:
				</p>
				<ul className="list-disc pl-5 space-y-1">
					<li>
						<strong>Consent:</strong>
						{" "}
						Where customers explicitly agree to data
						collection and processing.
					</li>
					<li>
						<strong>Contractual Necessity:</strong>
						{" "}
						When processing is essential
						to fulfill a financial service.
					</li>
					<li>
						<strong>Legal Obligation:</strong>
						{" "}
						To comply with regulatory
						requirements.
					</li>
					<li>
						<strong>Legitimate Interest:</strong>
						{" "}
						When processing is necessary
						for fraud prevention, security, and business operations.
					</li>
				</ul>

				<h3 className="text-lg font-medium mt-4 mb-1">5.2 Security Measures</h3>
				<p>Intasida implements robust security measures, including:</p>
				<ul className="list-disc pl-5 space-y-1">
					<li>Encryption of sensitive financial data.</li>
					<li>Multi-factor authentication (MFA) for customer accounts.</li>
					<li>Regular security audits and risk assessments.</li>
					<li>Employee training on data security and fraud prevention.</li>
				</ul>

				<h3 className="text-lg font-medium mt-4 mb-1">
					5.3 Data Minimization & Retention
				</h3>
				<p>
					We collect only necessary data and retain personal information for the
					duration required by law. Customer financial records are retained in
					line with the Nigerian Data Protection Act 2023 and other relevant
					regulations.
				</p>
			</div>
		)
	},
	{
		id: "responsibilities",
		title: "6. RESPONSIBILITIES OF INTASIDA",
		content: (
			<div>
				<p>Intasida shall ensure that personal data is:</p>
				<ul className="list-disc pl-5 space-y-1">
					<li>Processed in a fair, lawful and transparent manner.</li>
					<li>
						Collected for specified, explicit and legitimate purposes, and not
						to be further processed in a way incompatible with this purposes.
					</li>
					<li>
						Adequate, relevant and limited to the minimum necessary for the
						purposes for which the personal data was collected or further
						processed.
					</li>
					<li>
						Retained for not longer than is necessary to achieve the lawful
						bases for which the personal data was collected or further
						processed.
					</li>
					<li>
						Accurate, complete, not misleading, and, where necessary kept up to
						date having regard to the purposes for which the personal data is
						collected or is further processed.
					</li>
				</ul>
			</div>
		)
	},
	{
		id: "rights",
		title: "7. RIGHTS OF INDIVIDUALS",
		content: (
			<div>
				<p>Data subjects have the following rights:</p>
				<ul className="list-disc pl-5 space-y-1">
					<li>
						<strong>Right to Access:</strong>
						{" "}
						Request a copy of personal data
						processed by Intasida.
					</li>
					<li>
						<strong>Right to Rectification:</strong>
						{" "}
						Correct inaccurate personal
						data.
					</li>
					<li>
						<strong>Right to Erasure:</strong>
						{" "}
						Request deletion of data where no
						longer necessary.
					</li>
					<li>
						<strong>Right to Restriction:</strong>
						{" "}
						Limit processing in certain
						conditions.
					</li>
					<li>
						<strong>Right to Data Portability:</strong>
						{" "}
						Obtain and reuse data
						for personal purposes.
					</li>
					<li>
						<strong>Right to Object:</strong>
						{" "}
						Stop processing based on
						legitimate interests.
					</li>
				</ul>
			</div>
		)
	},
	{
		id: "third-party",
		title: "8. THIRD-PARTY PROCESSING",
		content: (
			<p>
				Intasida engages third-party service providers (e.g., payment
				processors, KYC verification services) under strict data processing
				agreements to ensure compliance with this policy and the Nigerian Data
				Protection Act 2023.
			</p>
		)
	},
	{
		id: "breaches",
		title: "9. REPORTING BREACHES",
		content: (
			<div>
				<p>In case of a data breach, Intasida will:</p>
				<ul className="list-disc pl-5 space-y-1">
					<li>Notify affected individuals and the NDPC within 72 hours.</li>
					<li>Investigate and mitigate risks associated with the breach.</li>
					<li>Take corrective actions to prevent future occurrences.</li>
				</ul>
			</div>
		)
	},
	{
		id: "compliance",
		title: "10. COMPLIANCE & AUDITS",
		content: (
			<div>
				<p>
					Intasida is committed to maintaining the highest standards of data
					protection and ensuring full compliance with the Nigerian Data
					Protection Act 2023. To this end, the company conducts periodic audits
					and assessments to evaluate its data processing activities, security
					measures, and overall adherence to regulatory requirements. These
					audits are designed to:
				</p>
				<ul className="list-disc pl-5 space-y-1">
					<li>
						Identify and mitigate potential risks associated with data handling
						and storage.
					</li>
					<li>
						Ensure that all data protection policies and procedures align with
						legal and industry standards.
					</li>
					<li>
						Assess the effectiveness of technical and organizational measures
						implemented to safeguard personal data.
					</li>
					<li>
						Verify compliance with data subject rights, including access,
						rectification, and erasure requests.
					</li>
					<li>
						Review third-party processors and service providers to confirm
						adherence to contractual and regulatory obligations.
					</li>
				</ul>
				<p className="mt-2">
					Any identified non-compliance issues are promptly addressed through
					corrective measures, and employees are regularly trained on best
					practices to uphold data protection principles. Intasida remains
					proactive in updating its policies and processes in response to
					evolving data protection laws and industry developments.
				</p>
			</div>
		)
	}
];
function RouteComponent() {
	const router = useRouter();
	const [activeLink, setActiveLink] = useState("home");
	const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
	useEffect(() => {
		const handleHashChange = () => {
			setActiveLink(router.history.location.hash.slice(1) || "home");
		};

		window.addEventListener("hashchange", handleHashChange);
		return () => window.removeEventListener("hashchange", handleHashChange);
	}, [router.history.location.hash]);

	return (
		<div className="bg-gradient-to-b from-white via-primary/10 to-violet-300">
			<motion.section
				initial={{ opacity: 0 }}
				animate={{ opacity: 1 }}
				transition={{ duration: 0.5 }}
				className="font-inter flex flex-col container mx-auto justify-between max-md:px-2"
			>
				<motion.div
					initial={{ y: -20, opacity: 0 }}
					animate={{ y: 0, opacity: 1 }}
					transition={{ duration: 0.5, delay: 0.2 }}
					className="flex items-center justify-between gap-4 py-4"
				>
					<img
						src="/logo-with-color-black.png"
						className="w-48"
						alt="Platform Logo"
					/>
					<div className="mx-auto flex-1" />
					<nav className="hidden md:flex gap-4 flex-1 bg-neutral-100 rounded-3xl p-1 border border-primary">
						<Button
							variant="link"
							className={`text-black hover:text-primary transition-all duration-200 ${activeLink === "home" ? "text-primary underline" : ""}`}
							asChild
						>
							<a
								href="#home"
								className="text-sm"
								onClick={() => setActiveLink("home")}
							>
								Home
							</a>
						</Button>

						<Button
							variant="link"
							className={`text-black hover:text-primary transition-all duration-200 ${activeLink === "features" ? "text-primary underline" : ""}`}
							asChild
						>
							<a
								href="#features"
								className="text-sm"
								onClick={() => setActiveLink("features")}
							>
								Features
							</a>
						</Button>
						<Button
							variant="link"
							className={`text-black hover:text-primary transition-all duration-200 ${activeLink === "about" ? "text-primary underline" : ""}`}
							asChild
						>
							<a
								href="#about"
								className="text-sm"
								onClick={() => setActiveLink("about")}
							>
								About us
							</a>
						</Button>
						<Button
							variant="link"
							className={`text-black hover:text-primary transition-all duration-200 ${activeLink === "contact" ? "text-primary underline" : ""}`}
							asChild
						>
							<a
								href="#contact"
								className="text-sm"
								onClick={() => setActiveLink("contact")}
							>
								Contact us
							</a>
						</Button>
						<Button
							variant="link"
							className={`text-black hover:text-primary transition-all duration-200 ${activeLink === "legal" ? "text-primary underline" : ""}`}
							asChild
						>
							<a
								href="#legal"
								className="text-sm"
								onClick={() => setActiveLink("legal")}
							>
								Legal
							</a>
						</Button>
						<Button className="rounded-full mr-2" asChild>
							<Link to="/sign-in" className="text-sm ml-auto">
								Sign up
							</Link>
						</Button>
					</nav>

					{/* Mobile Menu Button */}
					<Button
						variant="ghost"
						className="md:hidden"
						onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
					>
						<Menu className="h-6 w-6" />
					</Button>

					{/* Mobile Menu */}
					{isMobileMenuOpen && (
						<div className="md:hidden absolute top-16 left-0 right-0 bg-white border-b border-gray-200 shadow-lg z-50">
							<div className="flex flex-col p-4 space-y-2">
								<Button
									variant="link"
									className={`text-black hover:text-primary transition-all duration-200 ${activeLink === "home" ? "text-primary underline" : ""}`}
									asChild
								>
									<a
										href="#home"
										className="text-sm"
										onClick={() => {
											setActiveLink("home");
											setIsMobileMenuOpen(false);
										}}
									>
										Home
									</a>
								</Button>

								<Button
									variant="link"
									className={`text-black hover:text-primary transition-all duration-200 ${activeLink === "features" ? "text-primary underline" : ""}`}
									asChild
								>
									<a
										href="#features"
										className="text-sm"
										onClick={() => {
											setActiveLink("features");
											setIsMobileMenuOpen(false);
										}}
									>
										Features
									</a>
								</Button>

								<Button
									variant="link"
									className={`text-black hover:text-primary transition-all duration-200 ${activeLink === "about" ? "text-primary underline" : ""}`}
									asChild
								>
									<a
										href="#about"
										className="text-sm"
										onClick={() => {
											setActiveLink("about");
											setIsMobileMenuOpen(false);
										}}
									>
										About us
									</a>
								</Button>

								<Button
									variant="link"
									className={`text-black hover:text-primary transition-all duration-200 ${activeLink === "contact" ? "text-primary underline" : ""}`}
									asChild
								>
									<a
										href="#contact"
										className="text-sm"
										onClick={() => {
											setActiveLink("contact");
											setIsMobileMenuOpen(false);
										}}
									>
										Contact us
									</a>
								</Button>

								<Button
									variant="link"
									className={`text-black hover:text-primary transition-all duration-200 ${activeLink === "legal" ? "text-primary underline" : ""}`}
									asChild
								>
									<a
										href="#legal"
										className="text-sm"
										onClick={() => {
											setActiveLink("legal");
											setIsMobileMenuOpen(false);
										}}
									>
										Legal
									</a>
								</Button>

								<Button className="rounded-full" asChild>
									<Link to="/sign-in" className="text-sm">
										Sign up
									</Link>
								</Button>
							</div>
						</div>
					)}
				</motion.div>

				<motion.section
					initial={{ opacity: 0, y: 50 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.7, delay: 0.3 }}
					className="flex gap-4 min-h-96 mt-40 bg-primary/90 rounded-3xl text-white max-md:mt-4"
				>
					<div className="flex-1 px-8 py-4">
						<motion.h1
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.5 }}
							className="text-4xl leading-normal font-bold max-w-sm"
						>
							Protect Yourself from Fraudulent Vendors
						</motion.h1>
						<motion.p
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.5, delay: 0.4 }}
							className="opacity-80 mt-4 max-w-md"
						>
							You don't have to take a vendor's word for it. Simply ask them to pay through Intasida, and Intasida will act as a trusted intermediary to safeguard your transaction.

						</motion.p>
						<motion.div
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.5, delay: 0.6 }}
							className="flex  max-md:flex-col gap-4 mt-4"
						>
							<Button
								className="bg-black h-14 gap-2 text-white hover:bg-black/80"
								asChild
							>
								<a
									target="_blank"
									rel="noreferrer noopener"
									href="https://play.google.com/store/apps/details?id=com.intasida&pcampaignid=web_share"
								>
									<svg
										className="size-10"
										version="1.1"
										id="Layer_1"
										viewBox="0 0 511.999 511.999"
										fill="#000000"
									>
										<g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
										<g
											id="SVGRepo_tracerCarrier"
											strokeLinecap="round"
											strokeLinejoin="round"
										>
										</g>
										<g id="SVGRepo_iconCarrier">
											<g>
												<path
													fill="#32BBFF"
													d="M382.369,175.623C322.891,142.356,227.427,88.937,79.355,6.028 C69.372-0.565,57.886-1.429,47.962,1.93l254.05,254.05L382.369,175.623z"
												>
												</path>
												<path
													fill="#32BBFF"
													d="M47.962,1.93c-1.86,0.63-3.67,1.39-5.401,2.308C31.602,10.166,23.549,21.573,23.549,36v439.96 c0,14.427,8.052,25.834,19.012,31.761c1.728,0.917,3.537,1.68,5.395,2.314L302.012,255.98L47.962,1.93z"
												>
												</path>
												<path
													fill="#32BBFF"
													d="M302.012,255.98L47.956,510.035c9.927,3.384,21.413,2.586,31.399-4.103 c143.598-80.41,237.986-133.196,298.152-166.746c1.675-0.941,3.316-1.861,4.938-2.772L302.012,255.98z"
												>
												</path>
											</g>

											<path
												fill="#2C9FD9"
												d="M23.549,255.98v219.98c0,14.427,8.052,25.834,19.012,31.761c1.728,0.917,3.537,1.68,5.395,2.314 L302.012,255.98H23.549z"
											>
											</path>
											<path
												fill="#29CC5E"
												d="M79.355,6.028C67.5-1.8,53.52-1.577,42.561,4.239l255.595,255.596l84.212-84.212 C322.891,142.356,227.427,88.937,79.355,6.028z"
											>
											</path>
											<path
												fill="#D93F21"
												d="M298.158,252.126L42.561,507.721c10.96,5.815,24.939,6.151,36.794-1.789 c143.598-80.41,237.986-133.196,298.152-166.746c1.675-0.941,3.316-1.861,4.938-2.772L298.158,252.126z"
											>
											</path>
											<path
												fill="#FFD500"
												d="M488.45,255.98c0-12.19-6.151-24.492-18.342-31.314c0,0-22.799-12.721-92.682-51.809l-83.123,83.123 l83.204,83.205c69.116-38.807,92.6-51.892,92.6-51.892C482.299,280.472,488.45,268.17,488.45,255.98z"
											>
											</path>
											<path
												fill="#FFAA00"
												d="M470.108,287.294c12.191-6.822,18.342-19.124,18.342-31.314H294.303l83.204,83.205 C446.624,300.379,470.108,287.294,470.108,287.294z"
											>
											</path>
										</g>
									</svg>
									<div className="flex flex-col items-start justify-start">
										<span className="text-sm uppercase">Get it on</span>
										<span className="text-xl font-bold">Google Play</span>
									</div>
								</a>
							</Button>
							<Button
								className="bg-black h-14 gap-2 text-white hover:bg-black/80"
								asChild
							>
								<a
									target="_blank"
									rel="noreferrer noopener"
									href="https://apps.apple.com/us/app/intasida/id6740424971"
								>
									<svg
										className="size-10"
										fill="white"
										xmlns="http://www.w3.org/2000/svg"
										width="209"
										height="256"
										viewBox="0 0 814 1000"
									>
										<path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76.5 0-103.7 40.8-165.9 40.8s-105.6-57-155.5-127C46.7 790.7 0 663 0 541.8c0-194.4 126.4-297.5 250.8-297.5 66.1 0 121.2 43.4 162.7 43.4 39.5 0 101.1-46 176.3-46 28.5 0 130.9 2.6 198.3 99.2zm-234-181.5c31.1-36.9 53.1-88.1 53.1-139.3 0-7.1-.6-14.3-1.9-20.1-50.6 1.9-110.8 33.7-147.1 75.8-28.5 32.4-55.1 83.6-55.1 135.5 0 7.8 1.3 15.6 1.9 18.1 3.2.6 8.4 1.3 13.6 1.3 45.4 0 102.5-30.4 135.5-71.3z" />
									</svg>
									<div className="flex flex-col items-start justify-start">
										<span className="text-sm">Download on the</span>
										<span className="text-xl font-bold">App Store</span>
									</div>
								</a>
							</Button>
						</motion.div>
					</div>
					<motion.div
						initial={{ opacity: 0, x: 50 }}
						animate={{ opacity: 1, x: 0 }}
						transition={{ duration: 0.7, delay: 0.4 }}
						className="relative flex-1 max-lg:hidden"
					>
						<img
							src="/mock-phones.png"
							alt="Home Hero"
							className="absolute -top-32 right-20 max-w-xl"
						/>
					</motion.div>
				</motion.section>
				<motion.section
					initial={{ opacity: 0, y: 30 }}
					whileInView={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.6 }}
					viewport={{ once: true }}
					className="flex flex-col gap-4 items-center justify-center my-8"
				>
					<motion.h2
						initial={{ opacity: 0, y: 20 }}
						whileInView={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.5, delay: 0.2 }}
						viewport={{ once: true }}
						className="text-4xl max-md:text-2xl font-bold"
					>
						Built on Strong Partnership
					</motion.h2>
					<motion.p
						initial={{ opacity: 0, y: 20 }}
						whileInView={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.5, delay: 0.3 }}
						viewport={{ once: true }}
						className="text-lg text-gray-500 max-w-3xl max-md:text-center"
					>
						We're proud to work with a network of experts who share our vision.
					</motion.p>

					<motion.div
						initial={{ opacity: 0, y: 20 }}
						whileInView={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.5, delay: 0.4 }}
						viewport={{ once: true }}
						className="flex gap-4"
					>
						<div className="flex items-center justify-center p-4">
							<img src="/v.png" alt="VFD" className="max-h-12 object-contain" />
						</div>
						<div className="flex items-center justify-center p-4">
							<img
								src="/safehaven.svg"
								alt="Safe Haven"
								className="max-h-16 object-contain"
							/>
						</div>
						<div className="flex items-center justify-center p-4">
							<img
								src="/peace-mass.png"
								alt="Peace Mass"
								className="max-h-32 object-contain"
							/>
						</div>
					</motion.div>
				</motion.section>

				<section
					id="about"
					className="flex flex-col gap-4 mt-10 items-center justify-center"
				>
					<h2 className="text-4xl max-md:text-2xl font-bold">
						Why Choose Intasida ?
					</h2>
					<p className="text-lg text-gray-500 max-w-3xl text-center">
						We are revolutionizing e-commerce by creating a business environment where integrity thrives between buyers and vendors.
					</p>
					<div className="flex gap-4">
						<div className="flex-1"></div>
						<div className="flex-1"></div>
					</div>
				</section>
				<div className="min-h-screen py-16 px-4 sm:px-6 lg:px-8">
					<Features />
					<div className="max-w-7xl mx-auto">
						{/* Features Grid */}

						{/* How Intasida Works Section */}

						<Tabs defaultValue="buyer">
							<TabsList className="grid w-full max-w-md mx-auto grid-cols-2 bg-transparent gap-4">
								<TabsTrigger
									className="border border-primary rounded-full text-primary data-[state=active]:bg-primary data-[state=active]:text-white"
									value="buyer"
								>
									Buyer
								</TabsTrigger>
								<TabsTrigger
									className="border border-primary rounded-full text-primary data-[state=active]:bg-primary data-[state=active]:text-white"
									value="vendor"
								>
									Vendor
								</TabsTrigger>
							</TabsList>
							<TabsContent value="buyer">
								<Card className="shadow-none max-md:p-0">
									<CardContent className="space-y-2">
										<div className="bg-white rounded-3xl p-8 max-md:p-2">
											<div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
												<div>
													<h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-12 max-md:text-2xl max-md:my-4">
														Buying with Intasida
													</h2>

													<div className="space-y-8">
														{/* Step 1 */}
														<div className="flex gap-4">
															<div className="flex-shrink-0">
																<div className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center font-semibold">
																	1
																</div>
															</div>
															<div>
																<h3 className="text-xl font-semibold text-gray-900 mb-2">
																	Get a Contract Code
																</h3>
																<p className="text-gray-600 leading-relaxed">
																	Get a contract code from your vendor or from
																	the intasida Telegram group.
																</p>
															</div>
														</div>

														{/* Step 2 */}
														<div className="flex gap-4">
															<div className="flex-shrink-0">
																<div className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center font-semibold">
																	2
																</div>
															</div>
															<div>
																<h3 className="text-xl font-semibold text-gray-900 mb-2">
																	Search the Code
																</h3>
																<p className="text-gray-600 leading-relaxed">
																	Enter the code on the intasida app
																</p>
															</div>
														</div>

														{/* Step 3 */}
														<div className="flex gap-4">
															<div className="flex-shrink-0">
																<div className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center font-semibold">
																	3
																</div>
															</div>
															<div>
																<h3 className="text-xl font-semibold text-gray-900 mb-2">
																	Review and Pay
																</h3>
																<p className="text-gray-600 leading-relaxed">
																	Review the item properly and make payment.
																</p>
															</div>
														</div>
													</div>

													<Button className="rounded-full my-4">
														Get Started For Free
													</Button>
												</div>

												<div className="relative">
													<img
														src="/app-showcase-1.jpg"
														alt="Smiling vendor in marketplace with leather goods"
														width={500}
														height={600}
														className="rounded-2xl object-cover w-full h-[500px] lg:h-[600px]"
													/>
												</div>
											</div>
										</div>
									</CardContent>
								</Card>
							</TabsContent>
							<TabsContent value="vendor">
								<Card className="shadow-none">
									<CardContent className="space-y-2">
										<div className="bg-white rounded-3xl p-8">
											<div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
												<div className="relative">
													<img
														src="/iphone-photo.png"
														alt="Smiling vendor in marketplace with leather goods"
														width={500}
														height={600}
														className="rounded-2xl object-cover w-full h-[500px] lg:h-[600px]"
													/>
												</div>
												<div>
													<h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-12">
														Selling with Intasida
													</h2>

													<div className="space-y-8">
														{/* Step 1 */}
														<div className="flex gap-4">
															<div className="flex-shrink-0">
																<div className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center font-semibold">
																	1
																</div>
															</div>
															<div>
																<h3 className="text-xl font-semibold text-gray-900 mb-2">
																	Sign Up as a Vendor
																</h3>
																<p className="text-gray-600 leading-relaxed">
																	Create your Intasida account as a vendor
																</p>
															</div>
														</div>

														{/* Step 2 */}
														<div className="flex gap-4">
															<div className="flex-shrink-0">
																<div className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center font-semibold">
																	2
																</div>
															</div>
															<div>
																<h3 className="text-xl font-semibold text-gray-900 mb-2">
																	List Product
																</h3>
																<p className="text-gray-600 leading-relaxed">
																	Add the items you ant to sell.
																</p>
															</div>
														</div>

														{/* Step 3 */}
														<div className="flex gap-4">
															<div className="flex-shrink-0">
																<div className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center font-semibold">
																	3
																</div>
															</div>
															<div>
																<h3 className="text-xl font-semibold text-gray-900 mb-2">
																	Create a Contract ID
																</h3>
																<p className="text-gray-600 leading-relaxed">
																	Generate a contract using your product list.
																</p>
															</div>
														</div>
														<div className="flex gap-4">
															<div className="flex-shrink-0">
																<div className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center font-semibold">
																	4
																</div>
															</div>
															<div>
																<h3 className="text-xl font-semibold text-gray-900 mb-2">
																	Share and Send
																</h3>
																<p className="text-gray-600 leading-relaxed">
																	Send the contract ID to your buyer. they pay,
																	you drop off
																</p>
															</div>
														</div>
														<div className="flex gap-4">
															<div className="flex-shrink-0">
																<div className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center font-semibold">
																	5
																</div>
															</div>
															<div>
																<h3 className="text-xl font-semibold text-gray-900 mb-2">
																	Drop and Deliver
																</h3>
																<p className="text-gray-600 leading-relaxed">
																	Leave item at your nearest intasida location.
																	We handle the delivery
																</p>
															</div>
														</div>
														<div className="flex gap-4">
															<div className="flex-shrink-0">
																<div className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center font-semibold">
																	6
																</div>
															</div>
															<div>
																<h3 className="text-xl font-semibold text-gray-900 mb-2">
																	Get Paid Instantly
																</h3>
																<p className="text-gray-600 leading-relaxed">
																	Withdraw your money seamlessly from your
																	intasida wallet.
																</p>
															</div>
														</div>
													</div>

													<Button
														className="mt-8 bg-transparent text-primary hover:bg-purple-50 border border-primary px-6 py-2"
														asChild
													>
														<Link to="/sign-up">Get Started</Link>
													</Button>
												</div>
											</div>
										</div>
									</CardContent>
								</Card>
							</TabsContent>
						</Tabs>
					</div>
				</div>

				<HelpPage />

				<section className="flex flex-wrap gap-4 min-h-96 my-8 bg-primary/90 rounded-3xl text-white">
					<div className="flex-1 px-8 p-10 max-md:p-4 max-md:px-4">
						<p className="text-4xl font-bold mt-4 max-w-xl max-md:text-2xl">
							Say Goodbye To Social Media Shopping Scams And Hello To Trusted
							Transactions.
						</p>
						<div className="flex gap-4 flex-wrap mt-10">
							<Button
								className="bg-black h-14 gap-2 text-white hover:bg-black/80"
								asChild
							>
								<a
									target="_blank"
									rel="noreferrer noopener"
									href="https://play.google.com/store/apps/details?id=com.intasida&pcampaignid=web_share"
								>
									<svg
										className="size-10"
										version="1.1"
										id="Layer_1"
										viewBox="0 0 511.999 511.999"
										fill="#000000"
									>
										<g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
										<g
											id="SVGRepo_tracerCarrier"
											strokeLinecap="round"
											strokeLinejoin="round"
										>
										</g>
										<g id="SVGRepo_iconCarrier">
											<g>
												<path
													fill="#32BBFF"
													d="M382.369,175.623C322.891,142.356,227.427,88.937,79.355,6.028 C69.372-0.565,57.886-1.429,47.962,1.93l254.05,254.05L382.369,175.623z"
												>
												</path>
												<path
													fill="#32BBFF"
													d="M47.962,1.93c-1.86,0.63-3.67,1.39-5.401,2.308C31.602,10.166,23.549,21.573,23.549,36v439.96 c0,14.427,8.052,25.834,19.012,31.761c1.728,0.917,3.537,1.68,5.395,2.314L302.012,255.98L47.962,1.93z"
												>
												</path>
												<path
													fill="#32BBFF"
													d="M302.012,255.98L47.956,510.035c9.927,3.384,21.413,2.586,31.399-4.103 c143.598-80.41,237.986-133.196,298.152-166.746c1.675-0.941,3.316-1.861,4.938-2.772L302.012,255.98z"
												>
												</path>
											</g>

											<path
												fill="#2C9FD9"
												d="M23.549,255.98v219.98c0,14.427,8.052,25.834,19.012,31.761c1.728,0.917,3.537,1.68,5.395,2.314 L302.012,255.98H23.549z"
											>
											</path>
											<path
												fill="#29CC5E"
												d="M79.355,6.028C67.5-1.8,53.52-1.577,42.561,4.239l255.595,255.596l84.212-84.212 C322.891,142.356,227.427,88.937,79.355,6.028z"
											>
											</path>
											<path
												fill="#D93F21"
												d="M298.158,252.126L42.561,507.721c10.96,5.815,24.939,6.151,36.794-1.789 c143.598-80.41,237.986-133.196,298.152-166.746c1.675-0.941,3.316-1.861,4.938-2.772L298.158,252.126z"
											>
											</path>
											<path
												fill="#FFD500"
												d="M488.45,255.98c0-12.19-6.151-24.492-18.342-31.314c0,0-22.799-12.721-92.682-51.809l-83.123,83.123 l83.204,83.205c69.116-38.807,92.6-51.892,92.6-51.892C482.299,280.472,488.45,268.17,488.45,255.98z"
											>
											</path>
											<path
												fill="#FFAA00"
												d="M470.108,287.294c12.191-6.822,18.342-19.124,18.342-31.314H294.303l83.204,83.205 C446.624,300.379,470.108,287.294,470.108,287.294z"
											>
											</path>
										</g>
									</svg>
									<div className="flex flex-col items-start justify-start">
										<span className="text-sm uppercase">Get it on</span>
										<span className="text-xl font-bold">Google Play</span>
									</div>
								</a>
							</Button>
							<Button
								className="bg-black h-14 gap-2 text-white hover:bg-black/80"
								asChild
							>
								<a
									target="_blank"
									rel="noreferrer noopener"
									href="https://apps.apple.com/us/app/intasida/id6740424971"
								>
									<svg
										className="size-10"
										fill="white"
										xmlns="http://www.w3.org/2000/svg"
										width="209"
										height="256"
										viewBox="0 0 814 1000"
									>
										<path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76.5 0-103.7 40.8-165.9 40.8s-105.6-57-155.5-127C46.7 790.7 0 663 0 541.8c0-194.4 126.4-297.5 250.8-297.5 66.1 0 121.2 43.4 162.7 43.4 39.5 0 101.1-46 176.3-46 28.5 0 130.9 2.6 198.3 99.2zm-234-181.5c31.1-36.9 53.1-88.1 53.1-139.3 0-7.1-.6-14.3-1.9-20.1-50.6 1.9-110.8 33.7-147.1 75.8-28.5 32.4-55.1 83.6-55.1 135.5 0 7.8 1.3 15.6 1.9 18.1 3.2.6 8.4 1.3 13.6 1.3 45.4 0 102.5-30.4 135.5-71.3z" />
									</svg>
									<div className="flex flex-col items-start justify-start">
										<span className="text-sm">Download on the</span>
										<span className="text-xl font-bold">App Store</span>
									</div>
								</a>
							</Button>
						</div>
					</div>
					<div className="relative flex-1 flex items-center justify-center max-md:hidden">
						<img src="/products.png" alt="Products" className="max-w-lg " />
					</div>
				</section>
				<section id="legal">
					<Tabs defaultValue="privacy">
						<div className="relative  text-white rounded-3xl overflow-hidden h-96 w-full flex flex-col gap-4 my-auto justify-center items-start px-10">
							<img
								src="/legal.jpg"
								alt="Legal"
								className="absolute z-0 inset-0 object-cover w-full h-full"
							/>
							<h2 className="relative z-10 text-4xl font-bold ">Legal</h2>
							<p className="relative z-10 text-neutral-100 max-w-lg">
								We're committed to transparency and compliance with applicable
								laws and regulations.
							</p>
							<TabsList className="relative z-10 grid w-full max-w-md  grid-cols-2">
								<TabsTrigger value="privacy">Privacy Policy</TabsTrigger>
								<TabsTrigger value="terms">Terms & Conditions</TabsTrigger>
							</TabsList>
						</div>
						<TabsContent value="privacy">
							<Card className="shadow-none border-none bg-transparent">
								<CardContent className="space-y-2">
									<div className="flex justify-between my-8 items-center flex-wrap">
										<h4 className="text-2xl font-bold"> Privacy Policy</h4>
										<p className="text-gray-500">Last Updated: April 2025</p>
									</div>
									<div className="min-h-screen">
										<div className="mx-auto">
											<div className=" overflow-hidden rounded-lg">
												<div>
													<Accordion
														type="single"
														collapsible
														className="space-y-4"
													>
														{privacyPolicy.map(section => (
															<AccordionItem
																key={section.id}
																value={section.id}
																className="border border-gray-200 rounded-md overflow-hidden"
															>
																<AccordionTrigger className="px-4 py-3 bg-gray-50 hover:bg-gray-100 text-left font-medium text-primary">
																	{section.title}
																</AccordionTrigger>
																<AccordionContent className="px-4 py-4 bg-white text-gray-700">
																	{section.content}
																</AccordionContent>
															</AccordionItem>
														))}
													</Accordion>

													<div className="mt-8 pt-6 border-t border-gray-200 text-center text-sm text-gray-500">
														<p>
															&copy;
															{new Date().getFullYear()}
															{" "}
															Intasida Limited. All
															rights reserved.
														</p>
														<p className="mt-1">
															For questions regarding this policy, please
															contact our Data Protection Officer at
															privacy@intasida.com
														</p>
													</div>
												</div>
											</div>
										</div>
									</div>
								</CardContent>
							</Card>
						</TabsContent>
						<TabsContent value="terms">
							<Card className="shadow-none border-none bg-transparent p-0">
								<CardContent className="space-y-2">
									<div className="flex justify-between my-8 items-center">
										<h4 className="text-2xl font-bold">Terms & Conditions</h4>
									</div>
									<div>
										<div className="mx-auto">
											<div className=" overflow-hidden rounded-lg">
												<div>
													<Accordion
														type="single"
														collapsible
														className="space-y-4"
													>
														{termsAndConditions.map(section => (
															<AccordionItem
																key={section.id}
																value={section.id}
																className="border border-gray-200 rounded-md overflow-hidden"
															>
																<AccordionTrigger className="px-4 py-3 bg-gray-50 hover:bg-gray-100 text-left font-medium text-primary">
																	{section.title}
																</AccordionTrigger>
																<AccordionContent className="px-4 py-4 bg-white text-gray-700">
																	{section.content}
																</AccordionContent>
															</AccordionItem>
														))}
													</Accordion>
												</div>
											</div>
										</div>
									</div>
								</CardContent>
							</Card>
						</TabsContent>
					</Tabs>
				</section>
			</motion.section>

			{/* Footer Section */}
			<footer className="border-t bg-black border-gray-800 text-white">
				<div className=" text-white">
					{/* Call to Action Section */}
					<div className="px-6 py-16 text-center">
						<div className="mx-auto max-w-4xl">
							<h1 className="text-4xl font-bold mb-6 md:text-5xl lg:text-6xl">
								Get Value for Your Money and Time
							</h1>
							<p className="text-gray-300 text-lg mb-8 max-w-2xl mx-auto">

								Intasida not only protects you from losing your money but also saves you valuable time. If a vendor fails to drop your item at the courier within 24 hours after the ship-out date, Intasida gives you the option to cancel and receive an instant refund.
							</p>
							<Button
								size="lg"
								className="text-white px-8 py-3 text-lg rounded-full"
								asChild
							>
								<Link to="/sign-up">Get Started For Free</Link>
							</Button>
						</div>
					</div>
				</div>
				<div className="px-6 py-12">
					<div className="mx-auto max-w-7xl">
						<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
							{/* Company Info */}
							<div className="lg:col-span-1">
								<div className="flex items-center mb-4">
									<img
										src="/logo-with-color.png"
										className="w-48"
										alt="Platform Logo"
									/>
								</div>
								<p className="text-gray-400 text-sm leading-relaxed">
									Connect businesses with verified vendors for secure and
									reliable transactions worldwide.
								</p>
							</div>

							{/* Quick Links */}
							<div>
								<h3 className="text-lg font-semibold mb-4">Quick Links</h3>
								<ul className="space-y-3">
									<li>
										<a
											href="#"
											className="text-gray-400 hover:text-white transition-colors"
										>
											Home
										</a>
									</li>
									<li>
										<a
											href="#features"
											className="text-gray-400 hover:text-white transition-colors"
										>
											Features
										</a>
									</li>
									<li>
										<a
											href="#about"
											className="text-gray-400 hover:text-white transition-colors"
										>
											About Us
										</a>
									</li>
									<li>
										<a
											href="#contact"
											className="text-gray-400 hover:text-white transition-colors"
										>
											Contact Us
										</a>
									</li>
								</ul>
							</div>

							{/* Legal */}
							<div>
								<h3 className="text-lg font-semibold mb-4">Legal</h3>
								<ul className="space-y-3">
									<li>
										<a
											href="#"
											className="text-gray-400 hover:text-white transition-colors"
										>
											Privacy Policy
										</a>
									</li>
									<li>
										<a
											href="#"
											className="text-gray-400 hover:text-white transition-colors"
										>
											Terms of Service
										</a>
									</li>
								</ul>
							</div>

							{/* Contact Us */}
							<div>
								<h3 className="text-lg font-semibold mb-4">Contact Us</h3>
								<div className="space-y-3 text-gray-400">
									<p>info@intasida.com</p>
									<p>+234 705 587 6564</p>
									<p>123 Business Ave, Tech City</p>
								</div>
							</div>
						</div>

						{/* Bottom Section */}
						<div className="flex flex-col md:flex-row justify-between items-center mt-12 pt-8 border-t border-gray-800">
							<p className="text-gray-400 text-sm mb-4 md:mb-0">
								@2025 Secure vendor connect. All rights reserved.
							</p>
							<div className="flex space-x-4">
								<a
									href="https://www.instagram.com/official_intasida?igsh=M3c1cjE1c29mbTc5"
									target="_blank"
									rel="noreferrer noopener"
									className="text-gray-400 hover:text-white transition-colors"
								>
									<Instagram className="size-5" />
								</a>
								<a
									href="#"
									className="text-gray-400 hover:text-white transition-colors"
								>
									<Facebook className="size-5" />
								</a>
								<a
									href="https://x.com/official_intasida"
									target="_blank"
									rel="noreferrer noopener"
									className="text-gray-400 hover:text-white transition-colors"
								>
									<Twitter className="size-5" />
								</a>
							</div>
						</div>
					</div>
				</div>
			</footer>
		</div>
	);
}

export default function Features() {
	const features = [
		{
			title: "Secure Transactions",
			description:
        "End-to-end encryption and secure payment gateways to protect your financial data and ensure safe transactions.",
			bgColor: "bg-purple-100",
			iconBg: "bg-blue-600",
			icon: <Circle className="w-6 h-6 text-white fill-current" />,
			gridClass: "md:col-span-2 lg:col-span-2"
		},
		{
			title: "Secure Escrow Account",
			description:
        "Funds are held securely until contractual obligations and transaction are met.",
			bgColor: "bg-purple-200",
			iconBg: "bg-blue-600",
			icon: <Circle className="w-6 h-6 text-white fill-current" />,
			gridClass: "md:col-span-2"
		},
		{
			title: "Real Time Order Tracking",
			description:
        "Track your orders in real-time, including dispatch, transit, and delivery status directly from your buyer's dashboard",
			bgColor: "bg-white",
			iconBg: "bg-purple-600",
			icon: <Target className="w-6 h-6 text-white" />,
			gridClass: "md:col-span-1 md:row-span-1"
		},
		{
			title: "AI For Dispute Analysis",
			description:
        "Our AI feature automates the review process to speed up resolutions",
			bgColor: "bg-blue-700",
			iconBg: "bg-white",
			icon: <Circle className="w-6 h-6 text-blue-700 fill-current" />,
			textColor: "text-white",
			gridClass: "md:col-span-2 lg:col-span-2"
		},
		{
			title: "Integrated Logistics Network",
			description:
        "We integrated network of logistics agents to verify any product with contract terms before delivery to buyer.",
			bgColor: "bg-yellow-100",
			iconBg: "bg-purple-600",
			icon: <Target className="w-6 h-6 text-white" />,
			gridClass: "md:col-span-1"
		},
		{
			title: "Seamless Withdrawal",
			description:
        "Effortlessly withdraw your funds with easy steps directly to your bank mobile wallet within seconds.",
			bgColor: "bg-green-100",
			iconBg: "bg-purple-600",
			icon: <Target className="w-6 h-6 text-white" />,
			gridClass: "md:col-span-4"
		}
	];

	return (
		<motion.div
			initial={{ opacity: 0, y: 30 }}
			whileInView={{ opacity: 1, y: 0 }}
			transition={{ duration: 0.6 }}
			viewport={{ once: true }}
			className="p-6 max-w-7xl mx-auto max-md:p-0"
		>
			<section
				id="features"
				className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 auto-rows-fr my-6"
			>
				{features.map((feature, index) => (
					<motion.div
						key={feature.title}
						initial={{ opacity: 0, y: 20 }}
						whileInView={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.5, delay: index * 0.1 }}
						viewport={{ once: true }}
						className={`${feature.bgColor} ${feature.gridClass} rounded-2xl p-6 space-y-4 ${
							feature.textColor || "text-gray-900"
						} flex flex-col justify-between min-h-[200px]`}
					>
						<div className="space-y-4">
							<motion.div
								whileHover={{ scale: 1.1 }}
								transition={{ type: "spring", stiffness: 400, damping: 10 }}
								className={`${feature.iconBg} rounded-full w-12 h-12 flex items-center justify-center`}
							>
								{feature.icon}
							</motion.div>
							<div className="space-y-3">
								<h3 className="text-xl font-semibold leading-tight">
									{feature.title}
								</h3>
								<p
									className={`text-sm leading-relaxed ${feature.textColor ? "text-gray-200" : "text-gray-600"}`}
								>
									{feature.description}
								</p>
							</div>
						</div>
					</motion.div>
				))}
			</section>
		</motion.div>
	);
}
